'use client';
import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ArrowLeft } from 'lucide-react';

const LAST_UPDATED = 'June 2026';

const sectionStyle: React.CSSProperties = {
  borderTop: '1px solid #D8D8CE',
  paddingTop: 28,
};

const h2Style: React.CSSProperties = {
  fontFamily: 'var(--font-heading, "Arial Black", sans-serif)',
  fontSize: 14,
  fontWeight: 900,
  color: '#070707',
  marginBottom: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const pStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#5F625F',
  lineHeight: 1.75,
  fontFamily: 'var(--font-body, sans-serif)',
};

export default function PrivacyPolicyClient() {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F7F7F2' }}>
      <Header />
      <main style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px' }}>

          {/* Page header */}
          <div style={{ marginBottom: 40 }}>
            <Link href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#5F625F', textDecoration: 'none', marginBottom: 24, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              <ArrowLeft size={13} />
              {' '}Back to Home
            </Link>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12, backgroundColor: '#B8FF00', padding: '4px 10px', borderRadius: 2 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#070707', textTransform: 'uppercase', fontFamily: 'var(--font-mono, monospace)' }}>R3SET Running · Legal</span>
            </div>
            <h1 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 32, color: '#070707', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '-0.01em' }}>
              Privacy Policy
            </h1>
            <p style={{ fontSize: 12, color: '#5F625F', fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.04em' }}>Last updated: {LAST_UPDATED}</p>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '3px solid #111111', marginBottom: 40 }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

            <section style={sectionStyle}>
              <h2 style={h2Style}>1. Introduction</h2>
              <p style={pStyle}>R3SET Running ("we", "us", or "our") operates the website r3set.run and related community platforms. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, create an account, register for events, or participate in our community activities.</p>
              <p style={{ ...pStyle, marginTop: 12 }}>By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this policy, please do not access the site.</p>
            </section>

            <section style={sectionStyle}>
              <h2 style={h2Style}>2. Information We Collect</h2>
              <p style={{ ...pStyle, marginBottom: 12 }}>We may collect the following types of information:</p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  ['Account Information', 'Name, email address, password (encrypted), and profile details you provide during registration.'],
                  ['Profile Data', 'Running background, fitness goals, Instagram handle, WeChat ID, and other optional profile fields.'],
                  ['Session Participation', 'Records of training sessions you join, attendance history, and check-in data.'],
                  ['Event Registration', 'Information provided when registering for community events or races.'],
                  ['Contact Information', 'Messages sent through our contact form or email.'],
                  ['Usage Data', 'Pages visited, time spent on the site, browser type, device information, and IP address.'],
                  ['Cookies', 'Small data files stored on your device. See our Cookie Policy for details.'],
                ].map(([title, desc]) => (
                  <li key={title} style={{ display: 'flex', gap: 10, fontSize: 14, color: '#5F625F', lineHeight: 1.65, fontFamily: 'var(--font-body, sans-serif)' }}>
                    <span style={{ color: '#B8FF00', fontWeight: 900, flexShrink: 0, marginTop: 2 }}>—</span>
                    <span><strong style={{ color: '#070707', fontWeight: 700 }}>{title}:</strong> {desc}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={h2Style}>3. How We Use Your Information</h2>
              <p style={{ ...pStyle, marginBottom: 12 }}>We use the information we collect to:</p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {['Create and manage your member account', 'Process session registrations and track attendance', 'Send training schedules, session confirmations, and community updates', 'Respond to your inquiries and provide member support', 'Improve our website, training programs, and community features', 'Send administrative emails (account confirmation, password reset)', 'Analyse usage patterns to enhance user experience', 'Comply with legal obligations'].map((item) => (
                  <li key={item} style={{ display: 'flex', gap: 10, fontSize: 14, color: '#5F625F', lineHeight: 1.65, fontFamily: 'var(--font-body, sans-serif)' }}>
                    <span style={{ color: '#B8FF00', fontWeight: 900, flexShrink: 0 }}>—</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={h2Style}>4. Sharing Your Information</h2>
              <p style={{ ...pStyle, marginBottom: 12 }}>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following limited circumstances:</p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  ['Service Providers', 'Trusted third-party services that help us operate the platform (e.g., Supabase for database and authentication, email delivery services).'],
                  ['Community Display', 'Your display name and session participation may be visible to other community members within the platform.'],
                  ['Legal Requirements', 'When required by law or to protect the rights and safety of our community.'],
                ].map(([title, desc]) => (
                  <li key={title} style={{ display: 'flex', gap: 10, fontSize: 14, color: '#5F625F', lineHeight: 1.65, fontFamily: 'var(--font-body, sans-serif)' }}>
                    <span style={{ color: '#B8FF00', fontWeight: 900, flexShrink: 0, marginTop: 2 }}>—</span>
                    <span><strong style={{ color: '#070707', fontWeight: 700 }}>{title}:</strong> {desc}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={h2Style}>5. Data Retention</h2>
              <p style={pStyle}>We retain your personal data for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time by contacting us at hello@r3set.run. Some data may be retained for legal or operational purposes after account deletion.</p>
            </section>

            <section style={sectionStyle}>
              <h2 style={h2Style}>6. Data Security</h2>
              <p style={pStyle}>We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. Passwords are encrypted and never stored in plain text. However, no method of transmission over the internet is 100% secure.</p>
            </section>

            <section style={sectionStyle}>
              <h2 style={h2Style}>7. Your Rights</h2>
              <p style={{ ...pStyle, marginBottom: 12 }}>Depending on your location, you may have the right to:</p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {['Access the personal data we hold about you', 'Request correction of inaccurate data', 'Request deletion of your personal data', 'Object to or restrict processing of your data', 'Data portability (receive your data in a structured format)', 'Withdraw consent at any time where processing is based on consent'].map((item) => (
                  <li key={item} style={{ display: 'flex', gap: 10, fontSize: 14, color: '#5F625F', lineHeight: 1.65, fontFamily: 'var(--font-body, sans-serif)' }}>
                    <span style={{ color: '#B8FF00', fontWeight: 900, flexShrink: 0 }}>—</span>
                    {item}
                  </li>
                ))}
              </ul>
              <p style={{ ...pStyle, marginTop: 12 }}>To exercise any of these rights, please contact us at hello@r3set.run.</p>
            </section>

            <section style={sectionStyle}>
              <h2 style={h2Style}>8. Children's Privacy</h2>
              <p style={pStyle}>Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately.</p>
            </section>

            <section style={sectionStyle}>
              <h2 style={h2Style}>9. Changes to This Policy</h2>
              <p style={pStyle}>We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on this page with an updated date. Continued use of our services after changes constitutes acceptance of the updated policy.</p>
            </section>

            <section style={sectionStyle}>
              <h2 style={h2Style}>10. Contact Us</h2>
              <p style={{ ...pStyle, marginBottom: 16 }}>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
              <div style={{ padding: '16px 20px', border: '1px solid #D8D8CE', borderRadius: 2, backgroundColor: '#FFFFFF' }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#070707', marginBottom: 4, fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', textTransform: 'uppercase' }}>R3SET Running</p>
                <p style={{ fontSize: 13, color: '#5F625F', fontFamily: 'var(--font-mono, monospace)' }}>Email: hello@r3set.run</p>
                <p style={{ fontSize: 13, color: '#5F625F', fontFamily: 'var(--font-mono, monospace)' }}>Website: r3set.run</p>
              </div>
            </section>

          </div>

          <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid #D8D8CE', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {[
              { href: '/terms-of-use', label: 'Terms of Use' },
              { href: '/cookie-policy', label: 'Cookie Policy' },
              { href: '/contact', label: 'Contact' },
            ].map((item) => (
              <Link key={item.href} href={item.href} style={{ fontSize: 11, fontWeight: 700, color: '#5F625F', textDecoration: 'none', fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'color 0.15s ease' }}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
